import { PacketSelection, Button } from 'components'
import React, { Dispatch, FC, SetStateAction, useMemo, useState } from 'react'
import { isStandalonePrice } from 'helpers/getProductListDisplayInfo'
import { useTranslation } from 'react-i18next'
import { Order } from 'storeTypes'
import {
    PackageDiscount,
    ProductPriceType,
    ProductWithAddOns,
    SelectedPacket
} from './enrollFormTypes'
import { PacketQuantityView } from './PacketQuantityView'

type PacketSelectionPageProps = {
    selectedProduct: ProductWithAddOns
    selectedPackets: SelectedPacket[]
    onPacketSelection: Dispatch<SetStateAction<SelectedPacket[]>>
    handlePacketConfirmation: () => void
    onBackToProducts?: () => void
    isAddOn?: boolean
    fullGoalItems?: Order[]
    showDiscountBar?: boolean
}

const toSelectedPacket = (
    product: ProductWithAddOns,
    productPrice: ProductPriceType
): SelectedPacket => ({
    pricebook_id: productPrice.pricebook.sf_id,
    product_id: product.sf_id,
    price: productPrice.price?.toString(),
    size: productPrice.package_size?.toString(),
    size_display: productPrice.package_size_display,
    name: product.name,
    hidePrice: product.hide_price,
    unit: product.unit,
    package_id: productPrice.sf_id,
    uuid: undefined,
    image_url: product.image_url,
    isAddOn: false
})

export const PacketSelectionPage: FC<PacketSelectionPageProps> = ({
    selectedProduct,
    selectedPackets,
    onPacketSelection,
    handlePacketConfirmation,
    onBackToProducts,
    isAddOn = false,
    fullGoalItems = [],
    showDiscountBar = true
}) => {
    const { t } = useTranslation()

    const standalonePrices = useMemo(
        () => (selectedProduct.product_prices ?? []).filter(isStandalonePrice),
        [selectedProduct]
    )

    const isMultiSize = standalonePrices.length > 1

    const [selectedPrice, setSelectedPrice] = useState<ProductPriceType | undefined>(
        standalonePrices[0]
    )
    // Intentional: sheet opens on mount for multi-size products so the agent
    // must explicitly choose a size before the cart is seeded.
    const [sheetOpen, setSheetOpen] = useState(isMultiSize)

    // For multi-size products the agent must explicitly pick a size before
    // the cart is seeded. Single-size products auto-seed on mount.
    const [sizeConfirmed, setSizeConfirmed] = useState(!isMultiSize)

    const productPacket = useMemo(
        () => selectedPrice ? toSelectedPacket(selectedProduct, selectedPrice) : undefined,
        [selectedProduct, selectedPrice]
    )

    const discounts: PackageDiscount | undefined = selectedPrice?.discounts

    const handleSizeSelect = (price: ProductPriceType) => {
        setSelectedPrice(price)
        setSheetOpen(false)
        setSizeConfirmed(true)
    }

    // Backdrop may dismiss only after a size was confirmed (e.g. reopening
    // the sheet). Before the first pick, dismiss is a no-op so the agent
    // cannot skip size selection.
    const handleBackdropClick = () => {
        if (sizeConfirmed) {
            setSheetOpen(false)
        }
    }

    // If the agent taps Continue with 0 packets for this product across all
    // sizes, send them back to the product selection list instead of advancing.
    const handleContinue = () => {
        const productQty = selectedPackets.filter(
            (p) => p.product_id === selectedProduct.sf_id
        ).length
        if (productQty === 0 && onBackToProducts) {
            onBackToProducts()
            return
        }
        handlePacketConfirmation()
    }

    if (!isAddOn) {
        if (!productPacket) return null
        return (
            <div className="packet-selection-page">
                <PacketQuantityView
                    productPacket={productPacket}
                    discounts={discounts}
                    selectedPackets={selectedPackets}
                    onPacketSelection={onPacketSelection}
                    fullGoalItems={fullGoalItems}
                    showDiscountBar={showDiscountBar}
                    autoSeed={sizeConfirmed}
                    onContinue={handleContinue}
                    continueLabel={t('continue')}
                    onSizePress={isMultiSize ? () => setSheetOpen(true) : undefined}
                />

                {isMultiSize && sheetOpen && (
                    <>
                        <div
                            className="size-sheet__backdrop"
                            onClick={handleBackdropClick}
                        />
                        <div className="size-sheet">
                            <div className="size-sheet__handle" />
                            <h3 className="size-sheet__title">
                                {selectedProduct.name}
                            </h3>
                            <ul className="size-sheet__list">
                                {standalonePrices.map((price) => (
                                    <li key={price.sf_id}>
                                        <button
                                            className="size-sheet__row"
                                            aria-pressed={price.sf_id === selectedPrice?.sf_id}
                                            onClick={() => handleSizeSelect(price)}
                                        >
                                            <span className="size-sheet__size">
                                                {price.package_size_display}{' '}
                                                {price.units_of_measure}
                                            </span>
                                            {selectedPackets.filter((p) => p.package_id === price.sf_id).length > 0 && (
                                                <span className="size-sheet__count">
                                                    {selectedPackets.filter((p) => p.package_id === price.sf_id).length}
                                                </span>
                                            )}
                                            {!selectedProduct.hide_price && (
                                                <span className="size-sheet__price">
                                                    {price.price?.toLocaleString()} CFA
                                                </span>
                                            )}
                                            <span className="size-sheet__chevron" aria-hidden>
                                                ›
                                            </span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </>
                )}
            </div>
        )
    }

    return (
        <>
            <PacketSelection
                selectedProduct={selectedProduct}
                handlePacketSelection={onPacketSelection}
                preSelectedPackets={selectedPackets}
                isAddOn={isAddOn}
            />

            <div className="package-selection-footer">
                <Button
                    className="packet-selection--confirmation-button"
                    style="primary"
                    size="tall"
                    onClick={handlePacketConfirmation}
                >
                    {t('continue')}
                </Button>
            </div>
        </>
    )
}
